import { createContext, useContext } from "react";

import type { ModalPath } from "@/constants/Navigation";

type ModalLockSource = "DeepLinking" | "InactivityTimeout";

const ModalLockSources: Record<ModalLockSource, ModalLockSource> = {
  DeepLinking: "DeepLinking",
  InactivityTimeout: "InactivityTimeout",
} as const;

interface ModalTrackerContextProps {
  /**
   * Get the currently open modal identifier
   */
  readonly activeModal: null | ValueOf<typeof ModalPath>;

  /**
   * Show the native modals after a delay for animations to complete
   */
  allowNativeModalsAfterDelay: (source: ModalLockSource) => void;

  /**
   * Show the native modals with no delay, only to be used when it is safe to do so.
   */
  allowNativeModalsImmediate: (source: ModalLockSource) => void;

  /**
   * Whether the native modals should be hidden
   */
  isSafeToRenderNativeModals: boolean;

  /**
   * Hide the native modals
   */
  preventNativeModals: (source: ModalLockSource) => void;

  /**
   * Set the currently open modal
   */
  setActiveModal: (modalId: null | ValueOf<typeof ModalPath>) => void;
}

const ModalTrackerContext = createContext<ModalTrackerContextProps | undefined>(
  undefined,
);

/**
 * Hook to access modal tracking context
 * @throws Error if used outside of SafeNativeModalProvider
 */
const useModalTracker = (): ModalTrackerContextProps => {
  const context = useContext(ModalTrackerContext);
  if (!context) {
    throw new Error(
      "useModalTracker must be used within a SafeNativeModalProvider",
    );
  }
  return context;
};

export type { ModalLockSource, ModalTrackerContextProps };
export { ModalLockSources, ModalTrackerContext, useModalTracker };
